import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CogIcon } from "@heroicons/react/outline";
import type { RagAgent } from "src/features/rag/api/ragApi";
import { fetchAgent } from "src/features/rag/api/ragApi";
import WixarBadge from "src/features/rag/components/WixarBadge";
import { useRagDocuments } from "src/features/rag/hooks/useRagDocuments";
import RagDocumentManager from "src/features/rag/components/RagDocumentManager";
import RagChat from "src/features/rag/components/RagChat";
import Spinner from "src/common/components/Loader/Spinner";

const AGENT_ICONS: Record<string, string> = {
  default: "\uD83E\uDD16",
  book: "\uD83D\uDCDA",
  support: "\uD83C\uDFA7",
  brain: "\uD83E\uDDE0",
  search: "\uD83D\uDD0D",
  chat: "\uD83D\uDCAC",
  star: "\u2B50",
  tool: "\uD83D\uDD27",
  idea: "\uD83D\uDCA1",
  language: "\uD83C\uDF0D",
  education: "\uD83C\uDF93",
  expert: "\uD83D\uDC54",
};

const AgentDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [agent, setAgent] = useState<RagAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const agentId = id ? Number(id) : null;
  const ragDocsHook = useRagDocuments(agentId);
  const { documents } = ragDocsHook;

  const loadAgent = useCallback(async () => {
    if (!id) return;
    try {
      const data = await fetchAgent(Number(id));
      setAgent(data);
    } catch (err) {
      console.error("Failed to load agent:", err);
      navigate("/agents");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Reload agent when navigating back from settings (location.key changes)
  useEffect(() => {
    void loadAgent();
  }, [loadAgent, location.key]);

  if (loading) {
    return (
      <div className="h-full relative">
        <Spinner />
      </div>
    );
  }

  if (!agent) return null;

  const isShared = !!agent.is_shared;
  const hasReadyDocuments = isShared
    ? (agent.document_count ?? 0) > 0
    : documents.some((d) => d.status === "ready");
  const readyCount = isShared
    ? agent.document_count ?? 0
    : documents.filter((d) => d.status === "ready").length;
  const processingCount = isShared ? 0 : documents.filter((d) => d.status === "processing").length;

  return (
    <div className="flex h-full gap-4">
      {/* Left panel: Agent info + Documents */}
      <div className="w-1/2 min-w-[320px] max-w-[550px] flex flex-col gap-4">
        {/* Agent header card */}
        <div
          className={`bg-white rounded-xl border shadow-sm p-4 flex-shrink-0 ${
            isShared ? "border-blue-200" : "border-gray-200"
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${isShared ? "bg-blue-100" : "bg-green-100"}`}>
                <span className="text-xl leading-none">
                  {AGENT_ICONS[agent.icon] || AGENT_ICONS.default}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h2 className="text-base font-semibold text-gray-900">{agent.name}</h2>
                  {isShared && <WixarBadge />}
                </div>
                {agent.description && (
                  <p className="text-xs text-gray-500 mt-0.5">{agent.description}</p>
                )}
              </div>
            </div>
            {!isShared && (
              <button
                onClick={() => navigate(`/agents/${id}/settings`)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                aria-label={t("pages.agents.agentSettings")}
                title={t("pages.agents.agentSettings")}
              >
                <CogIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Mini stats */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              <span
                className={`inline-block w-2 h-2 rounded-full ${
                  readyCount > 0 ? "bg-green-500" : "bg-gray-300"
                }`}
              />
              <span className="text-xs text-gray-500">
                {readyCount} {t("pages.agents.ready").toLowerCase()}
              </span>
            </div>
            {processingCount > 0 && (
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse"
                  aria-hidden="true"
                />
                <span className="text-xs text-gray-500">
                  {processingCount} {t("pages.agents.processing").toLowerCase()}
                </span>
              </div>
            )}
            {agent.created_at && (
              <span className="text-xs text-gray-400 ml-auto">
                {t("pages.agents.createdAt", {
                  date: new Date(agent.created_at).toLocaleDateString(),
                })}
              </span>
            )}
          </div>
        </div>

        {/* Documents card — hidden for shared agents */}
        {!isShared && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex-1 min-h-0 flex flex-col">
            <RagDocumentManager agentId={agentId} hookData={ragDocsHook} />
          </div>
        )}
      </div>

      {/* Right panel: Chat */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col min-h-0">
        <RagChat agentId={agentId} hasDocuments={hasReadyDocuments} />
      </div>
    </div>
  );
};

export default AgentDetail;
